/**
 * The widget's copy, in both locales, written by hand.
 *
 * WHY THIS IS NOT IN THE SHARED CATALOGUE, and what it would take to move it.
 *
 * `packages/i18n/lingui.config.ts` extracts from `packages/i18n/src` and
 * `apps/web/src`. `apps/widget/src` is not on that list, and that file is not in
 * this agent's territory. A Lingui `<Trans>` macro here would therefore compile to
 * a message id that `lingui extract` never sees, `lingui compile` never emits and
 * `scripts/check-catalogs.mjs` never checks — and the visitor would read
 * `widget.composer.send` where the send button should be. A macro that silently
 * produces an untranslated string is worse than no macro, so this app keeps its
 * copy as data until one line is added to that `include` array.
 *
 * The property the shared pipeline gives for free — every id present in every
 * locale — is preserved here by `messages.test.ts`, which compares the two key
 * sets and fails on any difference. That is the gate, not a convention.
 *
 * NO ICU PLACEHOLDERS. Every value below is a literal string with no `{name}` and
 * no `plural`. Compiled catalogues carry pre-parsed ICU; these are raw, and
 * shipping the ICU parser into a widget that loads on every page view of a
 * customer's site to interpolate one number is not a trade worth making. Where a
 * number belongs in the UI it is rendered as its own element next to the text —
 * see the launcher badge, which is a digit the loader draws rather than a
 * sentence we would have to pluralise in Ukrainian's four forms.
 */
import { WIDGET_DEFAULT_LOCALE, type WidgetLocale } from './locales';
import { WIDGET_MESSAGE_IDS, type WidgetCatalog } from './ids';

// Re-exported so the dozen call sites that already import these from here do
// not all have to move; `ids.ts` says why the declarations themselves had to.
export { WIDGET_MESSAGE_IDS } from './ids';
export type { WidgetCatalog, WidgetMessageId } from './ids';

/**
 * English, EAGERLY — the one catalogue in the main bundle.
 *
 * Something has to render on the first frame. A visitor's own language is a
 * chunk that has not arrived yet, and a panel that paints empty labels for a
 * tick on somebody else's website is worse than one that paints English and
 * corrects itself.
 */
export { default as WIDGET_FALLBACK_CATALOG } from './catalogs/en';

/**
 * Every other language, as its OWN CHUNK.
 *
 * ── WHY THIS STOPPED BEING SYNCHRONOUS ────────────────────────────────────
 *
 * It used to be two object literals in this file, and `vendor/i18n` said
 * why: loading them over the network "would mean a visitor watching an empty
 * panel while a second request finishes, to save a few hundred bytes".
 *
 * That arithmetic was right for TWO catalogues and inverts for fifty. Shipping
 * all of them would put roughly fifty times the copy into a bundle that loads on
 * every page view of a customer's site, so that each visitor could read one
 * fiftieth of it — which is precisely the waste the 1.5 KB loader exists to
 * avoid. And the cost it was avoiding is avoided anyway: English is eager, so
 * the panel is never blank while the real catalogue is in flight.
 *
 * ── THE TEMPLATE LITERAL IS THE POINT ─────────────────────────────────────
 *
 * `import(\`./catalogs/${locale}.ts\`)` with the variable INSIDE the literal is
 * what lets the bundler enumerate the directory at build time and emit one chunk
 * per file. A variable holding the whole specifier defeats that: the bundler
 * cannot see the candidates, so it either inlines everything or fails to
 * resolve. The same constraint, for the same reason, is documented in
 * `packages/i18n/src/catalogs.ts`.
 */
export async function loadWidgetCatalog(locale: WidgetLocale): Promise<WidgetCatalog> {
  const module = (await import(`./catalogs/${locale}.ts`)) as { default: WidgetCatalog };
  return module.default;
}

/**
 * The id list and the catalogue directory agree — asserted, not assumed.
 *
 * Exported so `catalogs.test.ts` can walk every file with the same comparison
 * the old two-locale test made between `uk` and `en`. The property that
 * mattered has not changed: every id present in every locale. What changed is
 * that there are fifty of them and the check can no longer be done by reading.
 */
export function catalogDefects(catalog: Readonly<Record<string, string>>): readonly string[] {
  const problems: string[] = [];
  for (const id of WIDGET_MESSAGE_IDS) {
    const value = catalog[id];
    if (value === undefined) {
      problems.push(`missing: ${id}`);
    } else if (value.trim() === '') {
      // An empty string renders as nothing at all, which looks like a bug in the
      // panel rather than a gap in a catalogue.
      problems.push(`empty: ${id}`);
    }
  }
  for (const id of Object.keys(catalog)) {
    if (!(WIDGET_MESSAGE_IDS as readonly string[]).includes(id)) {
      problems.push(`unknown id: ${id}`);
    }
  }
  return problems;
}

export { WIDGET_DEFAULT_LOCALE };
export type { WidgetLocale };
